﻿import { Frequency, RRule, rrulestr, Weekday, type ByWeekday } from 'rrule'
import type {
  ICalDay,
  ICalendarEntry,
  ICalendarEntryApi,
  IConvertEventsToArrayPayload,
  IRRuleForm
} from '@/models/interfaces'
import { DateTime, Info, Interval } from 'luxon'
import i18n from '@/i18n'

// eslint-disable-next-line no-unused-vars
type FormatTimeInUserTz = (date: DateTime) => string

// display order for weekday lists, Sunday first
const WEEKDAY_DISPLAY_ORDER: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6
}

interface OrdinalSuffixMap {
  one?: string
  two?: string
  few?: string
  other: string
}

// ordinal suffixes by language code; add entries here as new locales require them
const ORDINAL_SUFFIXES: Record<string, OrdinalSuffixMap> = {
  en: { one: 'st', two: 'nd', few: 'rd', other: 'th' }
}

export default {
  isMultidayEventInDate(entry: ICalendarEntry, date: DateTime) {
    const _startDate = entry.startTime
    const _endDate = entry.endTime
    let isInDate = false
    if (_startDate.hasSame(date, 'day')) isInDate = true
    if (!isInDate && _endDate.hasSame(date, 'day')) isInDate = true
    if (Interval.fromDateTimes(_startDate, _endDate).contains(date))
      isInDate = true

    return isInDate
  },
  isRecurringEventInDate(entry: ICalendarEntry, date: DateTime) {
    const rruleSet = rrulestr(entry.rRule)
    const recurringEventArray = rruleSet.between(
      date.startOf('day').toJSDate(),
      date.endOf('day').toJSDate()
    )
    return recurringEventArray.length > 0
  },
  getFirstOccurrence(rrule: string, startDate: Date) {
    //JS Date object - Sunday represents 0, Monday 1, etc.
    let startDateDayOfWeek = startDate.getDay() - 1
    if (startDateDayOfWeek < 0) startDateDayOfWeek = 7

    const rruleSet = rrulestr(rrule)

    //however, 0 here is Monday
    //null byweekday is replaced by the start date day of week to ensure the difference is zero
    //origOptions byweekday seems to always work well, structure is always { weekday: number, n?: number }
    const byWeekday = rruleSet.origOptions.byweekday as
      | Weekday
      | Weekday[]
      | null
      | undefined
    const latestDayOfWeek = Math.max(
      ...(Array.isArray(byWeekday)
        ? byWeekday.map((w) => w.weekday)
        : [byWeekday?.weekday ?? startDateDayOfWeek])
    )

    const dayOfWeekDifference = latestDayOfWeek - startDateDayOfWeek

    //getting the latest day of the week's date
    const latestRecurrenceDate = DateTime.fromJSDate(startDate, { zone: 'utc' })
      .plus({ day: dayOfWeekDifference })
      .toJSDate()

    //ensuring the "first occurrence" gotten from .after is the latest weekday
    //daily frequencies should have byweekday be null, other frequencies start with a non-null byweekday
    if (rruleSet.origOptions?.byweekday)
      rruleSet.origOptions.byweekday = [latestDayOfWeek]

    const firstOcc = rruleSet.after(latestRecurrenceDate, false)
    return firstOcc
  },
  constructRRule(
    freq: string,
    until: string | null,
    interval: number,
    byweekday: string[],
    bysetpos: number | null
  ) {
    const rrule = new RRule({
      freq: this.getRRuleFreq(freq),
      until: until
        ? new Date(until) // make sure this date is already converted to user's timezone then converted to utc and string passed has no offset
        : null,
      interval: interval,
      byweekday: freq == 'Daily' ? [] : this.getRRuleByDay(byweekday),
      bysetpos: bysetpos
    })

    //console.log('returning RRule string...: ', rrule.toString())

    return rrule.toString()
  },
  getBySetPos(bysetpos: string) {
    switch (bysetpos) {
      case 'day':
      case undefined:
      case null:
        return null
      case 'lastday':
        return -1
      default:
        return parseInt(bysetpos)
    }
  },
  constructRRuleForDatePicker(
    freq: string,
    interval: number,
    byweekday: string[],
    bysetpos: number | null,
    dtStart: DateTime
  ) {
    const rrule = new RRule({
      freq: this.getRRuleFreq(freq),
      interval: interval,
      byweekday: this.getRRuleByDay(byweekday),
      bysetpos: bysetpos,
      dtstart: dtStart.toJSDate()
    })

    //console.log('constructed rrule string: ', rrule.toString())
    return rrule.toString()
  },
  deconstructRRule(rruleString: string): IRRuleForm {
    // strip trailing ';'
    const _rrule = RRule.fromString(rruleString)
    const options = _rrule.origOptions

    const byweekday = options.byweekday as
      | Weekday
      | Weekday[]
      | null
      | undefined
    const rruleForm = {
      freq: this.getDeconstructedRRuleFreq(options.freq),
      until: options.until,
      interval: options.interval ? options.interval : 1,
      byweekday: this.getDeconstructedRRuleDay(options.byweekday),
      bysetpos: options.bysetpos ?? null,
      n: Array.isArray(byweekday) ? byweekday[0].n : byweekday?.n
    }

    return rruleForm
  },
  getRRuleFreq(val: string) {
    switch (val) {
      case 'Daily':
        return RRule.DAILY
      case 'Weekly':
      case 'Every Other Week':
        return RRule.WEEKLY
      case 'Monthly':
        return RRule.MONTHLY
    }
  },
  getDeconstructedRRuleFreq(val: Frequency | undefined): string {
    let _text = ''
    switch (val) {
      case RRule.DAILY:
        _text = 'Daily'
        break
      case RRule.WEEKLY:
        _text = 'Weekly'
        break
      case RRule.MONTHLY:
        _text = 'Monthly'
        break
    }
    return _text
  },
  getDeconstructedRRuleDay(vals: Weekday[] | null | undefined) {
    if (vals) {
      const rule = vals.map(this.deconstructDayValue)
      return rule
    }
    return null
  },
  deconstructDayValue(val: Weekday) {
    //Weekday has two relevant attributes:
    //weekday: (0-6, 0 is monday)
    //n: number, for our purposes, -1 for last weekday, 1 for first weekday, 2 second, etc.; can be undefined

    switch (val.weekday) {
      case 0:
        return 'monday'
      case 1:
        return 'tuesday'
      case 2:
        return 'wednesday'
      case 3:
        return 'thursday'
      case 4:
        return 'friday'
      case 5:
        return 'saturday'
      case 6:
        return 'sunday'
    }
  },
  deconstructBySetPosValue(val: string | number | number[]) {
    switch (val) {
      case 1:
        return this.getCalendarText('calendar.form.repeat.monthPos.first')
      case 2:
        return this.getCalendarText('calendar.form.repeat.monthPos.second')
      case 3:
        return this.getCalendarText('calendar.form.repeat.monthPos.third')
      case 4:
        return this.getCalendarText('calendar.form.repeat.monthPos.fourth')
      case -1:
        return this.getCalendarText('calendar.form.repeat.monthPos.last')
      default:
        return ''
    }
  },
  getRRuleByDay(val: string[]): ByWeekday | ByWeekday[] | null | undefined {
    if (val) {
      const rule = val.map(this.getRRuleDayValue)
      return rule
    }
    return null
  },
  getRRuleDayValue(val: string): Weekday {
    let _weekday: Weekday

    let finalVal = val
    let n = parseInt(val) as number | null

    if (n && isNaN(n)) {
      n = null
    } else {
      finalVal = val.replace(`${n}`, '')
    }

    switch (finalVal) {
      case 'MO':
        _weekday = new Weekday(0, n ?? undefined)
        break
      case 'TU':
        _weekday = new Weekday(1, n ?? undefined)
        break
      case 'WE':
        _weekday = new Weekday(2, n ?? undefined)
        break
      case 'TH':
        _weekday = new Weekday(3, n ?? undefined)
        break
      case 'FR':
        _weekday = new Weekday(4, n ?? undefined)
        break
      case 'SA':
        _weekday = new Weekday(5, n ?? undefined)
        break
      case 'SU':
        _weekday = new Weekday(6, n ?? undefined)
        break
      default:
        _weekday = new Weekday(6, n ?? undefined)
    }
    return _weekday
  },
  getRRuleAsText(
    rruleString: string,
    startTime: DateTime,
    timeZoneId?: string
  ) {
    if (rruleString && rruleString.length > 0) {
      const _rruleString = rruleString.replace(/;\s*$/, '')
      const ruleObj = this.deconstructRRule(_rruleString)
      switch (ruleObj.freq) {
        case 'Monthly':
          return this.createMonthlyText(
            _rruleString,
            startTime,
            timeZoneId ?? 'utc'
          )
        case 'Weekly':
          return this.createWeeklyText(_rruleString, startTime, timeZoneId)
        case 'Daily':
          return this.createDailyText(_rruleString)
      }
    }
    return ''
  },
  createDailyText(rruleString: string) {
    // daily UNTIL text has always been shown in UTC (unlike weekly/monthly), preserved here for compatibility
    const ruleObj = this.deconstructRRule(rruleString)
    const count = ruleObj.interval ?? 1
    const untilDate = this.getUntilDateText(rruleString)

    return untilDate
      ? this.getCalendarText(
          'calendar.form.repeat.recurrenceText.daily.until',
          { count, untilDate },
          count
        )
      : this.getCalendarText(
          'calendar.form.repeat.recurrenceText.daily.default',
          { count },
          count
        )
  },
  createWeeklyText(
    rruleString: string,
    startTime: DateTime,
    timeZoneId?: string
  ) {
    const ruleObj = this.deconstructRRule(rruleString)
    const count = ruleObj.interval ?? 1
    const weekdayCount = ruleObj.byweekday?.length ?? 0
    const everyDay = weekdayCount === 7
    const startDate = this.formatMonthDayYear(startTime)
    const untilDate = this.getUntilDateText(rruleString, timeZoneId)
    const values = { count, startDate, untilDate }

    if (everyDay && count === 1) {
      return this.getCalendarText(
        untilDate
          ? 'calendar.form.repeat.recurrenceText.weekly.allDaysUntil'
          : 'calendar.form.repeat.recurrenceText.weekly.allDaysDefault',
        values
      )
    }

    if (everyDay) {
      return this.getCalendarText(
        untilDate
          ? 'calendar.form.repeat.recurrenceText.weekly.allDaysWithIntervalUntil'
          : 'calendar.form.repeat.recurrenceText.weekly.allDaysWithInterval',
        values
      )
    }

    if (weekdayCount > 0) {
      const days = this.parseByWeekday(ruleObj.byweekday)
      return this.getCalendarText(
        untilDate
          ? 'calendar.form.repeat.recurrenceText.weekly.withWeekdaysUntil'
          : 'calendar.form.repeat.recurrenceText.weekly.withWeekdays',
        { ...values, days },
        count
      )
    }

    return this.getCalendarText(
      untilDate
        ? 'calendar.form.repeat.recurrenceText.weekly.noWeekdaysUntil'
        : 'calendar.form.repeat.recurrenceText.weekly.noWeekdays',
      values,
      count
    )
  },
  createMonthlyText(
    rruleString: string,
    startTime: DateTime,
    timezone: string
  ) {
    const ruleObj = this.deconstructRRule(rruleString)
    const count = ruleObj.interval ?? 1
    const recurringOnLastDayOfMonth =
      ruleObj.bysetpos == -1 &&
      ruleObj.byweekday &&
      ruleObj.byweekday.length == 7
    const untilDate = this.getUntilDateText(rruleString, timezone)

    if (recurringOnLastDayOfMonth) {
      const month = this.formatMonth(startTime)
      return this.getCalendarText(
        untilDate
          ? 'calendar.form.repeat.recurrenceText.monthly.lastDayOfMonthUntil'
          : 'calendar.form.repeat.recurrenceText.monthly.lastDayOfMonth',
        { count, month, untilDate },
        count
      )
    }

    const startDate = this.formatMonthDayYear(startTime)

    if (ruleObj.n != undefined && ruleObj.byweekday?.length) {
      const weekday = this.parseByWeekday(ruleObj.byweekday)
      const position = this.deconstructBySetPosValue(ruleObj.n)
      return this.getCalendarText(
        untilDate
          ? 'calendar.form.repeat.recurrenceText.monthly.nthWeekdayUntil'
          : 'calendar.form.repeat.recurrenceText.monthly.nthWeekday',
        { count, position, weekday, startDate, untilDate },
        count
      )
    }

    if (ruleObj.byweekday?.length) {
      const weekday = this.parseByWeekday(ruleObj.byweekday)
      return this.getCalendarText(
        untilDate
          ? 'calendar.form.repeat.recurrenceText.monthly.eachWeekdayUntil'
          : 'calendar.form.repeat.recurrenceText.monthly.eachWeekday',
        { count, weekday, startDate, untilDate },
        count
      )
    }

    const ordinal = this.getDayOfMonthFormattedOrdinal(startTime.day)
    return this.getCalendarText(
      untilDate
        ? 'calendar.form.repeat.recurrenceText.monthly.dayOfMonthUntil'
        : 'calendar.form.repeat.recurrenceText.monthly.dayOfMonth',
      { count, ordinal, startDate, untilDate },
      count
    )
  },
  parseByWeekday(byweekday: string[]) {
    const orderedDays = [...byweekday].sort(
      (a, b) => WEEKDAY_DISPLAY_ORDER[a] - WEEKDAY_DISPLAY_ORDER[b]
    )
    const weekdays = orderedDays.map((day: string) =>
      this.getLocalizedWeekday(day)
    )

    // locale-aware conjunction/comma rules (e.g. Spanish "y"/"e", Oxford comma for English)
    return new Intl.ListFormat(this.getCurrentLocale(), {
      type: 'conjunction'
    }).format(weekdays)
  },
  getDayOfMonthFormattedOrdinal(dayOfMonth: number) {
    const locale = this.getCurrentLocale()
    const suffixes = ORDINAL_SUFFIXES[locale.split('-')[0]]

    // locales without an entry (e.g. Spanish) use the plain number, no suffix
    if (!suffixes) {
      return dayOfMonth.toString()
    }

    const category = new Intl.PluralRules(locale, {
      type: 'ordinal'
    }).select(dayOfMonth) as keyof OrdinalSuffixMap

    return dayOfMonth.toString() + (suffixes[category] ?? suffixes.other)
  },
  getRecurringEventSummary(entry: ICalendarEntry, recurrenceText: string) {
    if (this.isEventMultiDay(entry)) {
      return this.getCalendarText('calendar.form.repeat.summary.multiDayEvent', {
        recurrenceText
      })
    }

    if (entry.isAllDay) {
      return this.getCalendarText('calendar.form.repeat.summary.allDayEvent', {
        recurrenceText
      })
    }

    return this.getCalendarText('calendar.form.repeat.summary.defaultEvent', {
      recurrenceText
    })
  },
  formatEventTimes(
    startTime: DateTime,
    endTime: DateTime,
    isAllDay: boolean,
    timeZoneIdentifier: string,
    formatTimeInUserTz: FormatTimeInUserTz
  ) {
    const localizedStartTime = this.getLocalizedDateTime(startTime)
    const localizedEndTime = this.getLocalizedDateTime(endTime)

    if (isAllDay) {
      return this.getCalendarText('calendar.form.formattedTimes.allDayOn', {
        date: this.formatLongEventDate(localizedStartTime)
      })
    }

    if (!endTime.hasSame(startTime, 'day')) {
      return this.getCalendarText('calendar.form.formattedTimes.multiDay', {
        startDate: this.formatLongEventDate(localizedStartTime),
        startTime: localizedStartTime.toFormat('h:mm a'),
        endDate: this.formatLongEventDate(localizedEndTime),
        endTime: localizedEndTime.toFormat('h:mm a'),
        timeZone: timeZoneIdentifier
      })
    }

    return this.getCalendarText('calendar.form.formattedTimes.singleDay', {
      date: this.formatLongEventDate(localizedStartTime),
      startTime: formatTimeInUserTz(startTime),
      endTime: formatTimeInUserTz(endTime),
      timeZone: timeZoneIdentifier
    })
  },
  formatLongEventDate(date: DateTime) {
    const localizedDate = this.getLocalizedDateTime(date)

    if (this.isEnglishLocale()) {
      return localizedDate.toFormat(
        `cccc, MMMM '${this.getDayOfMonthFormattedOrdinal(date.day)}', yyyy`
      )
    }

    return localizedDate.toLocaleString({
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  },
  formatMonthDayYear(date: DateTime) {
    const localizedDate = this.getLocalizedDateTime(date)

    if (this.isEnglishLocale()) {
      return localizedDate.toFormat('MMMM d, yyyy')
    }

    return localizedDate.toLocaleString({
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  },
  formatMonth(date: DateTime) {
    const localizedDate = this.getLocalizedDateTime(date)

    if (this.isEnglishLocale()) {
      return localizedDate.toFormat('MMMM')
    }

    return localizedDate.toLocaleString({ month: 'long' })
  },
  getCurrentLocale() {
    return i18n.global.locale.value.toLowerCase()
  },
  isEnglishLocale() {
    return this.getCurrentLocale().startsWith('en')
  },
  getLocalizedDateTime(date: DateTime) {
    return date.setLocale(this.getCurrentLocale())
  },
  getCalendarText(
    key: string,
    values?: Record<string, string | number>,
    plural?: number
  ): string {
    return i18n.global.t(key, values ?? {}, plural) as string
  },
  getLocalizedWeekday(day: string) {
    // Info.weekdays() is Monday-indexed; convert using WEEKDAY_DISPLAY_ORDER (Sunday-first)
    const index = (WEEKDAY_DISPLAY_ORDER[day] + 6) % 7
    return Info.weekdays('long', { locale: this.getCurrentLocale() })[index]
  },
  getUntilDateText(rruleString: string, timezone = 'utc') {
    const untilStartPos = rruleString.indexOf('UNTIL=', 0)

    if (untilStartPos < 0) {
      return ''
    }

    const untilEndPos =
      rruleString.indexOf(';', untilStartPos) > -1
        ? rruleString.indexOf(';', untilStartPos)
        : rruleString.length
    const untilDate = rruleString.substring(untilStartPos + 6, untilEndPos)
    const untilDateObj = this.getLocalizedDateTime(
      DateTime.fromISO(untilDate, { zone: timezone })
    )

    if (this.isEnglishLocale()) {
      return untilDateObj.toFormat('MMMM d, yyyy')
    }

    return untilDateObj.toLocaleString({
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  },
  getMonthStartDate(year: number, month: number) {
    return DateTime.fromISO(`${year}-${month}-01`).startOf('week', {
      useLocaleWeeks: true
    })
  },
  getMonthEndDate(year: number, month: number) {
    return DateTime.fromISO(`${year}-${month}-01`)
      .endOf('month')
      .endOf('week', { useLocaleWeeks: true })
  },
  getEventsForDate(dateInUserTz: DateTime, events: ICalendarEntry[]) {
    // events for the date provided
    const _events = []
    for (let i = 0; i < events.length; i++) {
      let isInDate = false
      //check if event is all day, if so check against utc time
      if (events[i].isAllDay) {
        // if (events[i].StartTime.format('YYYY-MM-DD') == date) {
        //     isInDate = true
        // }
        if (events[i].startTime.hasSame(dateInUserTz, 'day')) {
          isInDate = true
        }
      } else {
        // regular event
        if (events[i].startTime.hasSame(dateInUserTz, 'day')) {
          isInDate = true
        }
        // multiday event
        if (!isInDate && events[i].endTime.hasSame(dateInUserTz, 'day')) {
          isInDate = true
        }
        // multiday event
        if (
          !isInDate &&
          Interval.fromDateTimes(
            events[i].startTime,
            events[i].endTime
          ).contains(dateInUserTz)
        )
          isInDate = true
      }
      if (isInDate) _events.push(events[i])
    }
    return _events
  },
  convertEventDatesToUsersLocalTime(
    events: ICalendarEntryApi[],
    timezone: string
  ): ICalendarEntry[] {
    const _entries: ICalendarEntry[] = JSON.parse(JSON.stringify(events))

    for (let i = 0; i < events.length; i++) {
      _entries[i].startTime = events[i].isAllDay
        ? DateTime.fromISO(events[i].startTime, { zone: 'utc' })
        : DateTime.fromISO(events[i].startTime, { zone: 'utc' }).setZone(
            timezone
          )
      _entries[i].endTime = events[i].isAllDay
        ? DateTime.fromISO(events[i].endTime, { zone: 'utc' })
        : DateTime.fromISO(events[i].endTime, { zone: 'utc' }).setZone(timezone)
      _entries[i].dtStart = events[i].dtStart
        ? events[i].isAllDay
          ? DateTime.fromISO(events[i].dtStart ?? '', { zone: 'utc' })
          : DateTime.fromISO(events[i].dtStart ?? '', { zone: 'utc' }).setZone(
              timezone
            )
        : null
      _entries[i].until = events[i].until
        ? DateTime.fromISO(events[i].until ?? '', { zone: 'utc' }).setZone(
            timezone
          )
        : null
      _entries[i].createdWhen = events[i].createdWhen
        ? DateTime.fromISO(events[i].createdWhen ?? '', {
            zone: 'utc'
          }).setZone(timezone)
        : null
      _entries[i].lastViewed = events[i].lastViewed
        ? DateTime.fromISO(events[i].lastViewed ?? '', { zone: 'utc' }).setZone(
            timezone
          )
        : null
      _entries[i].lastEdited = events[i].lastEdited
        ? DateTime.fromISO(events[i].lastEdited ?? '', { zone: 'utc' }).setZone(
            timezone
          )
        : null
    }
    return _entries
  },
  convertEventToUsersLocalTime(
    event: ICalendarEntryApi,
    timezone: string
  ): ICalendarEntry {
    const convertedEntry: ICalendarEntry = JSON.parse(JSON.stringify(event))
    return {
      ...convertedEntry,
      startTime: event.isAllDay
        ? DateTime.fromISO(event.startTime as string, { zone: 'utc' }).startOf(
            'day'
          )
        : DateTime.fromISO(event.startTime as string, { zone: 'utc' }).setZone(
            timezone
          ),
      endTime: event.isAllDay
        ? DateTime.fromISO(event.endTime as string, { zone: 'utc' })
        : DateTime.fromISO(event.endTime as string, { zone: 'utc' }).setZone(
            timezone
          ),
      dtStart:
        event.dtStart && event.dtStart.length > 0
          ? event.isAllDay
            ? DateTime.fromISO(event.dtStart ?? '', { zone: 'utc' })
            : DateTime.fromISO(event.dtStart ?? '', { zone: 'utc' }).setZone(
                timezone
              )
          : null,
      until:
        event.until && event.until.length > 0
          ? DateTime.fromISO(event.until, { zone: 'utc' }).setZone(timezone)
          : null,
      lastEdited: event.lastEdited
        ? DateTime.fromISO(event.lastEdited, { zone: 'utc' }).setZone(timezone)
        : null,
      lastViewed: event.lastViewed
        ? DateTime.fromISO(event.lastViewed, { zone: 'utc' }).setZone(timezone)
        : null,
      createdWhen: event.createdWhen
        ? DateTime.fromISO(event.createdWhen, { zone: 'utc' }).setZone(timezone)
        : null
    }
  },
  isDayInMonth(dayString: string, currentDay: DateTime) {
    return DateTime.fromISO(dayString).hasSame(currentDay, 'month')
  },
  isEventMultiDay(event: ICalendarEntry, countNewEvents?: boolean) {
    // done
    return (
      this.isMultiDay(event) &&
      !event.isAllDay &&
      (!event.isNew || countNewEvents)
    )
  },
  isEventSingleDay(event: ICalendarEntry, countNewEvents?: boolean) {
    // todo: delete after 3 column calendar
    // done
    return (
      this.isSingleDay(event) &&
      !event.isAllDay &&
      (!event.isNew || countNewEvents)
    )
  },
  isEventGreaterThanWeek(event: ICalendarEntry) {
    return (
      event.endTime.diff(event.startTime, 'days').days >= 7 &&
      !event.isAllDay &&
      !event.isNew
    )
  },
  isEventGreaterThanMonth(event: ICalendarEntry) {
    return (
      event.endTime == null ||
      event.startTime == null ||
      (event.endTime.diff(event.startTime, 'days').days > 27 &&
        !event.isAllDay &&
        !event.isNew)
    )
  },
  isEventForHourlyGrouping(event: ICalendarEntry, currentDay: DateTime) {
    return (
      this.isEventSingleDay(event) ||
      (this.isEventMultiDay(event) &&
        (event.startTime.hasSame(currentDay, 'day') ||
          event.endTime.hasSame(currentDay, 'day')))
    )
  },
  isEventAllDay(event: ICalendarEntry, currentDay: DateTime) {
    const dayStart = currentDay.startOf('day')
    const dayEnd = currentDay.endOf('day')

    // Use the same logic as grouping: all-day if marked as such OR covers full 24 hours
    return (
      event.isAllDay || (event.startTime <= dayStart && event.endTime >= dayEnd)
    )
  },
  isEventInDay(event: ICalendarEntry, day: string, timezone: string) {
    // if recurring, make sure the start date is the same as the request date
    if (event.isRecurring) {
      const startTime = event.isAllDay
        ? event.startTime.toUTC()
        : event.startTime.setZone(timezone)
      return startTime.day == DateTime.fromISO(day, { zone: 'utc' }).day
    }
    return true
  },
  isEventUnseen(event: ICalendarEntry) {
    // done
    return event.isNew
  },
  isMultiDay(event: ICalendarEntry) {
    // done
    return !event.endTime.hasSame(event.startTime, 'day') && !event.isAllDay
  },
  isSingleDay(event: ICalendarEntry) {
    return event.endTime.hasSame(event.startTime, 'day')
  },
  // gets the first day that shows up on month calendar (in specific timezone)
  getStartDateFromParamsForApiCall(
    dateTime: DateTime,
    timezone?: string
  ): DateTime {
    //
    const startDate = dateTime
      .setZone(timezone ?? 'utc')
      .startOf('month')
      .startOf('week', { useLocaleWeeks: true })
      .startOf('day')
    return startDate.toUTC()
  },
  // gets the last day that shows up on month calendar (in specific timezone)
  getEndDateFromParamsForApiCall(
    dateTime: DateTime,
    timezone?: string
  ): DateTime {
    //
    const endDate = dateTime
      .setZone(timezone ?? 'utc')
      .endOf('month')
      .endOf('week', { useLocaleWeeks: true })
      .endOf('day')
    return endDate.toUTC()
  },
  getLastOccurrenceBeforeUntil(
    rrule: string,
    start: DateTime,
    until: DateTime
  ) {
    const rruleObj = rrulestr(rrule, { dtstart: start.toJSDate() })

    return rruleObj.before(until.toJSDate(), true)
  },
  convertEventsToArrayOfDates(
    payload: IConvertEventsToArrayPayload,
    timezone: string
  ) {
    let _firstDate: DateTime
    let _endDate: DateTime

    if (typeof payload.startDate === 'string') {
      _firstDate = DateTime.fromISO(payload.startDate as unknown as string, {
        zone: 'utc'
      }).setZone(timezone)
    } else {
      _firstDate = payload.startDate
    }

    if (typeof payload.endDate === 'string') {
      _endDate = DateTime.fromISO(payload.endDate as unknown as string, {
        zone: 'utc'
      }).setZone(timezone)
    } else {
      _endDate = payload.endDate
    }

    let dateArray: ICalDay[] = []

    while (_firstDate <= _endDate) {
      const dayString = _firstDate.toISODate()
      if (dayString) {
        const calDay = {
          day: dayString,
          events: this.getEventsForDate(_firstDate, payload.events)
        }
        dateArray.push(calDay)
      }
      _firstDate = _firstDate.plus({ day: 1 })
    }

    return dateArray
  },
  /**
   * Groups events into clusters of intersecting events.
   * All all-day events are grouped together (they intersect with each other).
   * Timed events are grouped by overlap; non-intersecting events are single-item groups.
   * @param events Array of calendar events for a single day
   * @returns Array of event groups (each group is an array of intersecting events)
   */
  groupIntersectingEvents(events: ICalendarEntry[]): ICalendarEntry[][] {
    // Separate all-day and timed events
    const allDayEvents = events.filter((e) => e.isAllDay)
    const timedEvents = events.filter((e) => !e.isAllDay)

    // Group all-day events: all all-day events are considered intersecting with each other
    const allDayGroups: ICalendarEntry[][] =
      allDayEvents.length > 0 ? [allDayEvents] : []

    // Group timed events by overlap
    timedEvents.sort((a, b) => a.startTime.toMillis() - b.startTime.toMillis())
    const timedGroups: ICalendarEntry[][] = []
    for (const event of timedEvents) {
      let placed = false
      for (const group of timedGroups) {
        if (
          group.some(
            (e) => event.startTime < e.endTime && event.endTime > e.startTime
          )
        ) {
          group.push(event)
          placed = true
          break
        }
      }
      if (!placed) {
        timedGroups.push([event])
      }
    }

    // Return all groups (timed and all-day)
    return [...allDayGroups, ...timedGroups]
  },

  /**
   * Groups events for a specific day, treating timed events that span the full 24 hours of currentDate as all-day events.
   * All all-day events and full-day timed events are grouped together. Other timed events are grouped by overlap.
   * @param events Array of calendar events for a single day
   * @param currentDate The DateTime representing the current day
   * @returns Array of event groups (each group is an array of intersecting events)
   */
  /**
   * How this function works:
   * 1. It splits the input events into all-day events and timed events.
   * 2. For each timed event, if it covers the entire current day (starts at or before 00:00 and ends at or after 23:59:59.999)
   *    or is a multi-day event, it is treated as an all-day event for grouping purposes.
   * 3. All all-day events and full-day/multi-day timed events are grouped together in a single group.
   * 4. The remaining timed events are grouped by overlap: if two events overlap in time, they are placed in the same group.
   * 5. The function returns an array of groups: the first group contains all all-day and full-day/multi-day events, and the rest are groups of intersecting timed events.
   *
   * @param events Array of calendar events for a single day
   * @param currentDate The DateTime representing the current day
   * @returns Array of event groups (each group is an array of intersecting events)
   */
  groupIntersectingEventsSplitOfCurrentDay(
    events: ICalendarEntry[],
    currentDate: DateTime
  ): ICalendarEntry[][] {
    const dayStart = currentDate.startOf('day')
    const dayEnd = currentDate.endOf('day')

    // Step 1: Split events into true all-day events and full-day timed events
    const allDayEvents: ICalendarEntry[] = []
    const timedEvents: ICalendarEntry[] = []
    for (const e of events) {
      // If event is marked all-day, or covers the full 24 hours of currentDate, treat as all-day
      if (e.isAllDay || (e.startTime <= dayStart && e.endTime >= dayEnd)) {
        allDayEvents.push(e)
      } else {
        timedEvents.push(e)
      }
    }

    // Step 2: Group all-day events (including full-day timed events) together
    const allDayGroups: ICalendarEntry[][] =
      allDayEvents.length > 0 ? [allDayEvents] : []

    // Step 3: Group remaining timed events by overlap
    timedEvents.sort((a, b) => a.startTime.toMillis() - b.startTime.toMillis())
    const timedGroups: ICalendarEntry[][] = []
    for (const event of timedEvents) {
      let placed = false
      for (const group of timedGroups) {
        if (
          group.some(
            (e) => event.startTime < e.endTime && event.endTime > e.startTime
          )
        ) {
          group.push(event)
          placed = true
          break
        }
      }
      if (!placed) {
        timedGroups.push([event])
      }
    }

    return [...allDayGroups, ...timedGroups]
  }
}
